import { PortalTooltip } from "@/components/ui/PortalTooltip";
import { useInlineEdit } from "@/hooks/useInlineEdit";
import { cn } from "@paribus/shared-ui";
import { Pencil } from "lucide-react";
import React from "react";

export interface EditableTitleProps {
  value: string;
  /** Omit to render read-only: no pencil button, no double-click-to-edit. */
  onSave?: (next: string) => void | Promise<void>;
  as?: "h1" | "h2";
  /** Typography size classes (e.g. "text-2xl") — "font-bold" is always applied. */
  sizeClassName: string;
  truncate?: boolean;
  /** Shown in place of `value` when `value` is empty; renders no text if also omitted. */
  placeholder?: string;
  editAriaLabel?: string;
  /** Wrap the pencil button in a PortalTooltip showing `editAriaLabel`. */
  editTooltip?: boolean;
  /** Set the heading's native `title` HTML attribute as a hover tooltip instead of PortalTooltip. */
  nativeTitleAttribute?: boolean;
  inputFullWidth?: boolean;
  wrapperClassName?: string;
}

export const EditableTitle: React.FC<EditableTitleProps> = ({
  value,
  onSave,
  as = "h2",
  sizeClassName,
  truncate = true,
  placeholder,
  editAriaLabel = "Edit name",
  editTooltip = false,
  nativeTitleAttribute = false,
  inputFullWidth = false,
  wrapperClassName = "flex min-w-0 items-center gap-2",
}) => {
  const { isEditing, editValue, setEditValue, isSaving, startEditing, commitEdit, handleKeyDown } =
    useInlineEdit(value, onSave ?? (() => {}));

  if (isEditing) {
    return (
      <input
        autoFocus
        disabled={isSaving}
        value={editValue}
        onChange={(e) => setEditValue(e.target.value)}
        onBlur={commitEdit}
        onKeyDown={handleKeyDown}
        className={cn(
          "border-b-2 border-primary bg-transparent font-bold focus:outline-none disabled:opacity-60",
          sizeClassName,
          inputFullWidth && "w-full"
        )}
      />
    );
  }

  const Heading = as;
  const displayText = value || placeholder || "";

  const heading = (
    <Heading
      className={cn(
        "font-bold",
        as === "h1" && "tracking-tight",
        sizeClassName,
        truncate && "truncate",
        onSave && "cursor-pointer"
      )}
      onDoubleClick={onSave ? startEditing : undefined}
      title={nativeTitleAttribute ? displayText || "Double-click to edit" : undefined}
    >
      {displayText}
    </Heading>
  );

  if (!onSave) {
    return heading;
  }

  const pencilButton = (
    <button
      type="button"
      aria-label={editAriaLabel}
      onClick={startEditing}
      className="shrink-0 rounded-md p-1 text-text-disabled transition-colors hover:bg-neutral-100 hover:text-muted-foreground focus-visible:shadow-focus"
    >
      <Pencil size={16} aria-hidden />
    </button>
  );

  return (
    <div className={wrapperClassName}>
      {heading}
      {editTooltip ? (
        <PortalTooltip label={editAriaLabel}>{pencilButton}</PortalTooltip>
      ) : (
        pencilButton
      )}
    </div>
  );
};
